import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MyTodosService } from "@services/myTodos/MyTodosService";
import { CommentService } from "@services/comment/CommentService";
import { appStore } from "@store/appStore";
import { LaunchAd } from "@models/launchAd";
import { EngagementAction } from "@models/engagementAction";
import Loader from "src/components/common/loader/loader";
import showAlert from "src/shared/utils/generic";
import ReviewPanelHeader from "./components/ReviewPanelHeader";
import ContainerPreviewTabs from "./components/ContainerPreviewTabs";
import ActionCtas from "./components/ActionCtas";
import ReviewCommentList from "src/modules/createEditor/components/ReviewCommentList";
import ReviewCommentInput from "./components/ReviewCommentInput";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./styles.scss";

export default function ReviewPanelPage() {
  const { actionId } = useParams();
  const navigate = useNavigate();

  const actionIdNum = Number(actionId);

  useDocumentTitle("Review");

  const [launchAd, setLaunchAd] = useState<LaunchAd | null>(null);
  const [action, setAction] = useState<EngagementAction | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentKey, setCommentKey] = useState(0);
  const [commentBody, setCommentBody] = useState("");
  // How many comments this GuidePost already carries (MPN-1626).
  const [commentCount, setCommentCount] = useState(0);
  const [postingComment, setPostingComment] = useState(false);
  const commentBodyRef = useRef(commentBody);
  commentBodyRef.current = commentBody;
  const inFlightPost = useRef<Promise<void> | null>(null);

  const postComment = async (todoId: number, text: string) => {
    const orgId = appStore.loggedInUser?.organization?.id;
    if (!orgId) throw new Error("Missing organization id");
    await new CommentService().postComment(orgId, todoId, text, []);
  };

  /*
      The one path that sends what is in the box, shared by the send button and
      by Approve / Request changes. When a send is already in flight it is
      awaited rather than repeated: clicking send and then Approve before the
      first request came back used to post the same comment twice, because the
      box is only cleared once the response lands.
  */
  const postPendingComment = async () => {
    if (inFlightPost.current) {
      await inFlightPost.current;
      return;
    }
    const text = commentBodyRef.current.trim();
    const todoId = launchAd?.review_todo?.id;
    if (!text || !todoId) return;

    const post = postComment(todoId, text).then(() => {
      setCommentBody("");
      setCommentKey((k) => k + 1);
    });
    inFlightPost.current = post;
    try {
      await post;
    } finally {
      inFlightPost.current = null;
    }
  };

  const submitComment = async () => {
    if (postingComment) return;
    setPostingComment(true);
    try {
      await postPendingComment();
    } catch (e: any) {
      showAlert(e?.message ?? "Failed to post comment", "error", 4000);
    } finally {
      setPostingComment(false);
    }
  };

  /*
      Runs before approve / request changes. A failure here propagates on
      purpose: the reviewer's words are part of the decision, so the decision
      does not get recorded without them.
  */
  const flushPendingComment = async () => {
    try {
      await postPendingComment();
    } catch {
      throw new Error("Your comment could not be posted, so nothing was submitted. Try again.");
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const la = await new MyTodosService().getActionReview(actionIdNum);
      setLaunchAd(la);
      setAction(la.my_action);
      setUserRoles(la.my_action ? la.my_action.assigned_roles : []);
    } catch {
      setError("Failed to load review.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!actionId) return;
    load();
     
  }, [actionId]);

  if (loading) {
    return (
      <div className="mt-review-page">
        <ReviewPanelHeader breadcrumb="Loading…" />
        <div className="mt-review-page__body"><Loader /></div>
      </div>
    );
  }

  if (error || !launchAd) {
    return (
      <div className="mt-review-page">
        <ReviewPanelHeader breadcrumb="Error" />
        <div className="mt-review-page__body">
          <div className="mt-page__error">
            {error ?? "Unknown error"}
            <button onClick={load}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumb = action
    ? `${action.engagement_name} · ${action.launch_name} · ${action.ad_name}`
    : (launchAd.ad?.name ?? `Action ${actionIdNum}`);

  const children = launchAd.ad?.children ?? [];

  return (
    <div className="mt-review-page">
      <ReviewPanelHeader breadcrumb={breadcrumb} status={launchAd.status} />
      <div className="mt-review-page__body">
        <div className="mt-review-page__preview">
          <ContainerPreviewTabs children={children} />
          {/*
              Where this ad's traffic goes (MPN-1536).

              The client is approving the destination as much as the creative, and until
              now the panel showed the creative alone. The URL is taken from the server's
              resolved value, tag included or not, rather than rebuilt here from the
              toggle: the ticket requires the approval view and the published ad to agree,
              and they can only be guaranteed to agree if there is one source for both.
          */}
          {launchAd.destination_url && (
            <div className="mt-review-page__destination">
              <span className="mt-review-page__destination-label">Clicks go to</span>
              <a
                href={launchAd.destination_url}
                target="_blank"
                rel="noopener noreferrer"
                title={launchAd.destination_url}
              >
                {launchAd.destination_url}
              </a>
            </div>
          )}
        </div>
        <div className="mt-review-page__review">
          <div style={{ flex: 1, overflow: "auto" }}>
            <ReviewCommentList
              rootAd={launchAd.ad}
              refreshKey={commentKey}
              onCountChange={setCommentCount}
            />
          </div>
          {launchAd.review_todo?.id && (
            <ReviewCommentInput
              value={commentBody}
              onChange={setCommentBody}
              onSubmit={submitComment}
              submitting={postingComment}
            />
          )}
          <ActionCtas
            action={action}
            userRoles={userRoles}
            onCompleted={() => navigate("/my-todos")}
            beforeMutation={flushPendingComment}
            /*
                Reject with nothing to act on and the client is stopped (MPN-1626).
                Satisfied by either what is in the box or a comment already on the ad:
                the box is the ordinary path, since Request changes flushes it, but
                someone who typed and pressed send first has already said their piece
                and being told to comment again would be wrong.
            */
            requestChangesNeedsComment={!commentBody.trim() && commentCount === 0}
          />
        </div>
      </div>
    </div>
  );
}
